/**
 * File: RouteLayouts.js
 * Origin: isotect
 * Description: React layout components that render an outlet for nested routes.
 *
 * Version: v2.0.0
 * Created: 2026-04-21
 * Last Updated: 2026-06-23
 *
 * Used By: isotect
 * Maintainer: Zachary Dicks
 *
 * Notes:
 * - Changes in this file may affect all consuming frontend modules.
 * - See Git repository 'isotect' for full change history.
 */

// Third-party packages
import { Navigate, Outlet } from "react-router-dom";
import { useState } from "react";

// Local project files
import UserSideNavigation from "../navigation/UserSideNavigation";

/**
 * Protected route layout that renders authenticated routes.
 *
 * Redirects unauthenticated users to a specified URL.
 *
 * @component
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isAuth - Indicates whether the user is authenticated
 * @param {string} props.navigateUrl - URL to redirect unauthenticated users to
 * @param {Object} props.navigationSchema - Navigation schema configuration
 *
 * @returns {JSX.Element} Rendered protected route layout or redirect element
 */
function AuthLayout({ isAuth, navigateUrl, navigationSchema }) {
    const [isOpen, setIsOpen] = useState(true);

    if (!isAuth) {
        return <Navigate to={navigateUrl} replace />;
    }

    return (
        <>
            <div className="auth-layout">
                <UserSideNavigation
                    schema={navigationSchema}
                    isOpen={isOpen}
                    setIsOpen={setIsOpen}
                />
                <div className="auth-layout__outlet">
                    <Outlet />
                </div>
            </div>
        </>
    );
}

/**
 * Public route layout that renders accessible routes.
 *
 * Renders public pages and nested routes without authentication checks.
 *
 * @component
 *
 * @returns {JSX.Element} Rendered public route layout with an outlet for nested routes
 */
function PublicLayout() {
    return (
        <>
            <Outlet />
        </>
    );
}

export { AuthLayout, PublicLayout };
